import React, { useState, useMemo } from "react";
import Papa from "papaparse";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import dayjs from "dayjs";

/**
 * Simple GSTR-1 UI page:
 * - Upload CSV (InvoiceNET export or similar)
 * - Shows preview table (first 200 rows)
 * - Filter by date range
 * - Export mapped GSTR-1 JSON as ZIP (client-side)
 *
 * This mapper is intentionally minimal and tolerant. Validate resulting JSON
 * with GST portal offline validator before submission.
 */

function parseDateToISO(d) {
  if (!d) return null;
  const s = String(d).trim();
  // try common formats: dd-mm-yyyy, dd/mm/yyyy, yyyy-mm-dd
  const t = s.replace(/\//g, "-");
  const parts = t.split("-");
  if (parts.length === 3) {
    if (parts[2].length === 4) return `${parts[2]}-${parts[1].padStart(2, "0")}-${parts[0].padStart(2, "0")}`;
    if (parts[0].length === 4) return `${parts[0]}-${parts[1].padStart(2, "0")}-${parts[2].padStart(2, "0")}`;
  }
  const dt = dayjs(s);
  return dt.isValid() ? dt.format("YYYY-MM-DD") : null;
}

function toNumber(v) {
  if (v === undefined || v === null) return 0;
  const s = String(v).replace(/,/g, "").trim();
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

// Basic mapping: rows -> b2b grouping by GSTIN; b2cs for empty GSTIN
function mapRowsToGSTR1(rows) {
  const byCtin = {};
  const b2cs = [];

  rows.forEach((raw) => {
    // normalize keys lowercase
    const row = {};
    Object.keys(raw).forEach((k) => { row[k.trim().toLowerCase()] = raw[k]; });

    const gstin = (row["gstin"] || row["buyer gstin"] || row["ecommerce gstin"] || "").toString().trim();
    const docNo = (row["document no"] || row["documentno"] || row["invoice no"] || row["document no."] || row["document_number"] || "").toString().trim();
    const docDate = parseDateToISO(row["document date"] || row["invoice date"] || row["date"] || row["documentdate"]);
    const taxable = toNumber(row["taxable value"] || row["taxable"] || row["taxableval"] || row["taxable value"] || row["taxable_value"]);
    const igst = toNumber(row["igst amt"] || row["igstamt"] || row["igst"]);
    const cgst = toNumber(row["cgst amt"] || row["cgstamt"] || row["cgst"]);
    const sgst = toNumber(row["sgst amt"] || row["sgstamt"] || row["sgst"]);
    const rate = toNumber(row["gst rate"] || row["gstrt"] || row["gst"] || row["rate"]);
    const place = (row["place of supply"] || row["place"] || "").toString().trim();
    const party = (row["party"] || row["party name"] || row["buyer name"] || "").toString().trim();
    const invoiceAmount = toNumber(row["invoice amount"] || row["amount"] || row["invoiceamount"]);

    const total = Number((taxable + igst + cgst + sgst).toFixed(2)) || Number(invoiceAmount.toFixed(2)) || 0;

    const inv = {
      inum: docNo || `NA-${Math.random().toString(36).slice(2, 8)}`,
      idt: docDate || null,
      val: total,
      pos: place || "",
      rt: rate,
      inv_typ: "R",
      itms: [
        {
          num: 1,
          itm_det: {
            rt: rate,
            txval: Number(taxable.toFixed(2)),
            iamt: Number(igst.toFixed(2)),
            camt: Number(cgst.toFixed(2)),
            samt: Number(sgst.toFixed(2)),
            hsn: (row["hsn"] || row["hsn code"] || "").toString().trim(),
          },
        },
      ],
      partyName: party,
    };

    if (gstin && gstin.length >= 15) {
      const ctin = gstin.toUpperCase();
      if (!byCtin[ctin]) byCtin[ctin] = [];
      byCtin[ctin].push(inv);
    } else {
      b2cs.push(inv);
    }
  });

  const b2b = Object.keys(byCtin).map((ctin) => ({ ctin, inv: byCtin[ctin] }));
  // Simple b2cs structure: list of invoices
  const b2csMapped = b2cs.map((inv) => ({ inv: [inv] }));

  return { b2b, b2cs: b2csMapped };
}

export default function Gstr1Page() {
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [startDate, setStartDate] = useState(dayjs().startOf("month").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const filtered = useMemo(() => {
    if (!rows || rows.length === 0) return [];
    return rows.filter((r) => {
      const dateField = r["Document Date"] || r["document date"] || r["Invoice Date"] || r["date"] || r["documentdate"];
      const iso = parseDateToISO(dateField);
      if (!iso) return true; // keep if can't parse
      const d = dayjs(iso);
      const from = dayjs(startDate);
      const to = dayjs(endDate);
      return d.isBetween(from.subtract(1, "day"), to.add(1, "day"), null, "[]");
    });
  }, [rows, startDate, endDate]);

  const handleFile = (file) => {
    setError(null);
    if (!file) return;
    setFileName(file.name);
    setLoading(true);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        setLoading(false);
        if (results && results.errors && results.errors.length) {
          console.warn("parse errors", results.errors);
          setError("Some parsing errors occurred — check console.");
        }
        setRows(results.data || []);
      },
      error: (err) => {
        setLoading(false);
        console.error(err);
        setError("File parse failed: " + err.message);
      },
    });
  };

  const handleFileInput = (e) => {
    const f = e.target.files?.[0];
    handleFile(f);
  };

  const handleDrop = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    const f = ev.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const handleExport = async () => {
    try {
      if (!rows || rows.length === 0) return alert("Upload CSV first");
      setLoading(true);
      // filter first
      const useRows = filtered.length ? filtered : rows;
      const gstr1 = mapRowsToGSTR1(useRows);
      const zip = new JSZip();
      zip.file("GSTR1.json", JSON.stringify(gstr1, null, 2));
      const blob = await zip.generateAsync({ type: "blob" });
      saveAs(blob, `GSTR1_${dayjs().format("YYYY-MM-DD")}.zip`);
    } catch (err) {
      console.error("export error", err);
      setError("Export failed: " + (err.message || err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4">
      <h2 className="text-xl font-semibold mb-3">GSTR-1 Export (Client-side) 📤</h2>

      <div className="mb-3 space-y-2">
        <div className="flex gap-2 items-center">
          <label className="px-3 py-2 border rounded cursor-pointer bg-white">
            Select CSV
            <input type="file" accept=".csv,.txt" onChange={handleFileInput} className="hidden" />
          </label>
          <div className="text-sm text-gray-600">{fileName || "No file chosen"}</div>
        </div>

        <div
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          className="p-3 border-dashed border-2 border-gray-300 rounded text-sm text-gray-600"
        >
          Or drag & drop CSV here
        </div>

        <div className="flex gap-3 items-center mt-2">
          <label className="text-sm">From: <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="ml-2 border px-2 py-1" /></label>
          <label className="text-sm">To: <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="ml-2 border px-2 py-1" /></label>
          <button onClick={() => { setStartDate(dayjs().startOf("month").format("YYYY-MM-DD")); setEndDate(dayjs().format("YYYY-MM-DD")); }} className="ml-2 px-3 py-1 border rounded">This month</button>
          <button onClick={() => { setStartDate(""); setEndDate(""); }} className="ml-2 px-3 py-1 border rounded">Clear dates</button>
        </div>

        <div className="flex gap-2 mt-2">
          <button onClick={handleExport} disabled={loading || !rows.length} className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50">
            {loading ? "Processing..." : "Export GSTR-1 ZIP"}
          </button>
          <button onClick={() => { setRows([]); setFileName(""); }} className="px-4 py-2 border rounded">Clear</button>
        </div>

        {error && <div className="text-red-600 mt-2">{error}</div>}
        <div className="text-xs text-gray-500 mt-1">Preview shows first 200 rows. Validate result with GST offline tool before upload.</div>
      </div>

      <div className="border rounded overflow-auto max-h-[60vh]">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100 sticky top-0">
            <tr>
              {filtered.length ? Object.keys(filtered[0] || {}).slice(0, 40).map((k) => (<th key={k} className="p-2 border text-left text-xs">{k}</th>)) :
                (rows.length ? Object.keys(rows[0] || {}).slice(0, 40).map((k) => (<th key={k} className="p-2 border text-left text-xs">{k}</th>)) : <th className="p-2">No data</th>)
              }
            </tr>
          </thead>
          <tbody>
            {(filtered.length ? filtered : rows).slice(0, 200).map((r, idx) => (
              <tr key={idx} className="odd:bg-white even:bg-gray-50">
                {Object.keys(r).slice(0, 40).map((k) => (
                  <td key={k} className="p-1 border text-xs">{String(r[k] ?? "").slice(0, 80)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 text-sm text-gray-600">
        Rows loaded: {rows.length}. Filtered shown: {filtered.length || rows.length}
      </div>
    </div>
  );
}

// helper exported for testing or reuse
export function mapRowsToGSTR1Wrapper(rows) {
  return mapRowsToGSTR1(rows);
}
